// AABB-vs-voxel (+ dynamic box colliders) movement shared by the player and mobs.
import { BLOCKS } from './blocks.js';

const EPS = 0.001;

// body: { pos:Vector3 (feet centre), vel:Vector3, hw, h, onGround, groundId, platform, hitWall, hitCeil }
// colliders: [{ min:{x,y,z}, max:{x,y,z}, solid:true, ... }]
export function moveBody(body, world, colliders, dx, dy, dz) {
  body.onGround = false;
  body.hitWall = false;
  body.hitCeil = false;
  body.platform = null;
  const m = Math.max(Math.abs(dx), Math.abs(dy), Math.abs(dz));
  const steps = Math.max(1, Math.ceil(m / 0.3));
  const sx = dx / steps, sy = dy / steps, sz = dz / steps;
  for (let i = 0; i < steps; i++) {
    if (sy !== 0) moveAxis(body, world, colliders, 1, sy);
    if (sx !== 0) moveAxis(body, world, colliders, 0, sx);
    if (sz !== 0) moveAxis(body, world, colliders, 2, sz);
  }
  // ground probe: are we resting on something? (handles sy == 0 cases)
  if (!body.onGround && body.vel.y <= 0) {
    const id = groundBelow(body, world, colliders, 0.02);
    if (id) {
      body.onGround = true;
      if (id !== -1) body.groundId = id;
    }
  }
}

function moveAxis(b, world, colliders, axis, d) {
  const p = b.pos;
  if (axis === 0) p.x += d;
  else if (axis === 1) p.y += d;
  else p.z += d;
  const minX = p.x - b.hw, maxX = p.x + b.hw;
  const minY = p.y, maxY = p.y + b.h;
  const minZ = p.z - b.hw, maxZ = p.z + b.hw;
  const x0 = Math.floor(minX + EPS), x1 = Math.floor(maxX - EPS);
  const y0 = Math.floor(minY + EPS), y1 = Math.floor(maxY - EPS);
  const z0 = Math.floor(minZ + EPS), z1 = Math.floor(maxZ - EPS);
  for (let y = y0; y <= y1; y++)
    for (let z = z0; z <= z1; z++)
      for (let x = x0; x <= x1; x++) {
        const id = world.get(x, y, z);
        if (!id || !BLOCKS[id].solid) continue;
        const shape = BLOCKS[id].shape;
        const bounds = shape ? shape.find(q=>maxX>x+q[0]+EPS&&minX<x+q[3]-EPS&&maxY>y+q[1]+EPS&&minY<y+q[4]-EPS&&maxZ>z+q[2]+EPS&&minZ<z+q[5]-EPS) : [0,0,0,1,1,1];
        if (!bounds) continue;
        const [a,b0,c,d0,e,f]=bounds;
        if (axis === 0) {
          p.x = d > 0 ? x+a - b.hw - EPS : x+d0 + b.hw + EPS;
          b.vel.x = 0;
          b.hitWall = true;
        } else if (axis === 1) {
          if (d < 0) {
            p.y = y + e;
            b.onGround = true;
            b.groundId = id;
          } else {
            p.y = y+b0 - b.h - EPS;
            b.hitCeil = true;
          }
          b.vel.y = 0;
        } else {
          p.z = d > 0 ? z+c - b.hw - EPS : z+f + b.hw + EPS;
          b.vel.z = 0;
          b.hitWall = true;
        }
        return;
      }
  if (!colliders) return;
  for (const c of colliders) {
    if (!c.solid || c === b.self) continue;
    if (maxX <= c.min.x || minX >= c.max.x || maxY <= c.min.y || minY >= c.max.y || maxZ <= c.min.z || minZ >= c.max.z) continue;
    if (axis === 0) {
      p.x = d > 0 ? c.min.x - b.hw - EPS : c.max.x + b.hw + EPS;
      b.vel.x = 0;
      b.hitWall = true;
    } else if (axis === 1) {
      if (d < 0) {
        p.y = c.max.y;
        b.onGround = true;
        b.platform = c;
        b.groundId = c.blockId || 0;
      } else {
        p.y = c.min.y - b.h - EPS;
        b.hitCeil = true;
      }
      b.vel.y = 0;
    } else {
      p.z = d > 0 ? c.min.z - b.hw - EPS : c.max.z + b.hw + EPS;
      b.vel.z = 0;
      b.hitWall = true;
    }
    if (c.onTouch) c.onTouch(b, axis, d);
    return;
  }
}

// Returns block id under the body (or -1 for a collider), 0 if nothing
export function groundBelow(b, world, colliders, probe = 0.05) {
  const p = b.pos;
  const yy = Math.floor(p.y - probe);
  const x0 = Math.floor(p.x - b.hw + EPS), x1 = Math.floor(p.x + b.hw - EPS);
  const z0 = Math.floor(p.z - b.hw + EPS), z1 = Math.floor(p.z + b.hw - EPS);
  for (let z = z0; z <= z1; z++)
    for (let x = x0; x <= x1; x++) {
      const id = world.get(x, yy, z);
      if (id && BLOCKS[id].solid) {
        const shapes=BLOCKS[id].shape||[[0,0,0,1,1,1]];
        if(shapes.some(q=>p.y>=yy+q[4]-.01&&p.y-yy-q[4]<=probe+.01&&p.x+b.hw>x+q[0]&&p.x-b.hw<x+q[3]&&p.z+b.hw>z+q[2]&&p.z-b.hw<z+q[5])) return id;
      }
    }
  if (colliders)
    for (const c of colliders) {
      if (!c.solid || c === b.self) continue;
      if (p.y < c.max.y - 0.01 || p.y - c.max.y > probe + 0.01) continue;
      if (p.x + b.hw <= c.min.x || p.x - b.hw >= c.max.x || p.z + b.hw <= c.min.z || p.z - b.hw >= c.max.z) continue;
      b.platform = c;
      return -1;
    }
  return 0;
}

// Push a body out of any dynamic collider it overlaps (for platforms moving into the player)
export function depenetrate(b, colliders) {
  const p = b.pos;
  for (const c of colliders) {
    if (!c.solid || c === b.self) continue;
    const minX = p.x - b.hw, maxX = p.x + b.hw, minY = p.y, maxY = p.y + b.h, minZ = p.z - b.hw, maxZ = p.z + b.hw;
    if (maxX <= c.min.x || minX >= c.max.x || maxY <= c.min.y || minY >= c.max.y || maxZ <= c.min.z || minZ >= c.max.z) continue;
    const ox1 = c.max.x - minX, ox2 = maxX - c.min.x;
    const oy1 = c.max.y - minY, oy2 = maxY - c.min.y;
    const oz1 = c.max.z - minZ, oz2 = maxZ - c.min.z;
    const ox = Math.min(ox1, ox2), oy = Math.min(oy1, oy2), oz = Math.min(oz1, oz2);
    if (oy <= ox && oy <= oz) {
      if (oy1 < oy2) {
        p.y += oy1 + EPS;
        b.vel.y = Math.max(0, b.vel.y);
      } else p.y -= oy2 + EPS;
    } else if (ox <= oz) p.x += ox1 < ox2 ? ox1 + EPS : -(ox2 + EPS);
    else p.z += oz1 < oz2 ? oz1 + EPS : -(oz2 + EPS);
  }
}

// Is an AABB free of solid voxels?
export function boxFree(world, minX, minY, minZ, maxX, maxY, maxZ) {
  for (let y = Math.floor(minY + EPS); y <= Math.floor(maxY - EPS); y++)
    for (let z = Math.floor(minZ + EPS); z <= Math.floor(maxZ - EPS); z++)
      for (let x = Math.floor(minX + EPS); x <= Math.floor(maxX - EPS); x++) {
        const id = world.get(x, y, z);
        if (id && BLOCKS[id].solid && (BLOCKS[id].shape||[[0,0,0,1,1,1]]).some(q=>maxX>x+q[0]+EPS&&minX<x+q[3]-EPS&&maxY>y+q[1]+EPS&&minY<y+q[4]-EPS&&maxZ>z+q[2]+EPS&&minZ<z+q[5]-EPS)) return false;
      }
  return true;
}
